/**
 * A voice note's waveform. Measured in the browser (the only place the recording can be
 * decoded), stored on the message as one base-36 character per bar — `0` silent to `z`
 * loudest — and drawn by `Waveform`. The pure parts live here so they can be tested; the
 * decoding part needs a real AudioContext.
 */

export const PEAK_BARS = 80;
const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';

/** Float32 samples → `bars` levels in 0..1, loudest bar = 1, quiet parts lifted a little. */
export function peaksFromSamples(samples, bars = PEAK_BARS) {
  const n = samples?.length || 0;
  if (!n || bars < 1) return [];
  const out = new Array(bars).fill(0);
  const per = n / bars;
  for (let b = 0; b < bars; b += 1) {
    const from = Math.floor(b * per);
    const to = Math.max(from + 1, Math.floor((b + 1) * per));
    let sum = 0;
    let count = 0;
    for (let i = from; i < to && i < n; i += 1) { const v = samples[i]; sum += v * v; count += 1; }
    out[b] = count ? Math.sqrt(sum / count) : 0;
  }
  const max = Math.max(...out);
  if (!(max > 0)) return out.map(() => 0);
  // A square root lifts the quiet stretches so a soft voice still shows a shape.
  return out.map((v) => Math.sqrt(v / max));
}

/** 0..1 levels → the stored string. */
export function encodePeaks(levels) {
  return (levels || []).map((v) => ALPHABET[Math.max(0, Math.min(35, Math.round((Number.isFinite(v) ? v : 0) * 35)))]).join('');
}

/** The stored string → 0..1 levels; null for anything that isn't one. */
export function decodePeaks(str) {
  if (typeof str !== 'string' || str.length < 4) return null;
  const out = [];
  for (const c of str) {
    const i = ALPHABET.indexOf(c);
    if (i < 0) return null;
    out.push(i / 35);
  }
  return out;
}

/** Fewer bars for a small card: each new bar is the loudest of the ones it covers. */
export function downsamplePeaks(levels, bars) {
  if (!levels?.length || bars >= levels.length) return levels || [];
  const out = [];
  const per = levels.length / bars;
  for (let b = 0; b < bars; b += 1) {
    const from = Math.floor(b * per);
    const to = Math.max(from + 1, Math.floor((b + 1) * per));
    let m = 0;
    for (let i = from; i < to; i += 1) m = Math.max(m, levels[i]);
    out.push(m);
  }
  return out;
}

/** The flat line a note shows before anyone has measured it. */
export function placeholderPeaks(bars) {
  return new Array(bars).fill(0.12);
}

/** Decode a recording (Blob or ArrayBuffer) and measure it. Resolves null when the browser can't. */
export async function measurePeaks(source, bars = PEAK_BARS, { timeoutMs = 6000 } = {}) {
  const Ctx = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
  if (!Ctx) return null;
  let ctx = null;
  try {
    const buf = source instanceof ArrayBuffer ? source : await source.arrayBuffer();
    ctx = new Ctx();
    const decoded = await Promise.race([
      new Promise((resolve, reject) => { ctx.decodeAudioData(buf.slice(0), resolve, reject); }),
      new Promise((_, reject) => { setTimeout(() => reject(new Error('decode timeout')), timeoutMs); }),
    ]);
    const samples = decoded.getChannelData(0);
    return encodePeaks(peaksFromSamples(samples, bars));
  } catch {
    return null;
  } finally {
    try { ctx?.close(); } catch { /* already closed */ }
  }
}
